import { Table, Form, Input, Space, Typography } from 'antd';
import { useForm } from 'antd/es/form/Form';
import dayjs from 'dayjs';
import { ChangeEvent, Dispatch, memo, SetStateAction, useMemo } from 'react';

import { EditIcon } from 'assets/svg';
import { ButtonAction } from 'components';
import useQueryParam from 'hooks/useQueryPrams';
import {
  IPurchaseDetailData,
  TpurchaseDetail,
  TPurchaseDetail,
} from 'interfaces/PurchaseInvoice';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import formatNumber, {
  formatCurrency,
  formatDate,
  totalData,
} from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';

const { Text } = Typography;
const { Summary } = Table;
const { SaveTwoTone, CloseCircleTwoTone } = icons;

interface PurchaseTableProps {
  editIndex: number;
  data: IPurchaseDetailData;
  setEditIndex: Dispatch<SetStateAction<number>>;
  onSave: (data: IPurchaseInvoiceDetailForm & { detailId: string }) => void;
  migoList?: any[];
  lastMigoStatus?: boolean;
  lastMigoType?: string;
}

export interface IPurchaseInvoiceDetailForm {
  measureWeightThreadAccounting: string;
  lossWeightThreadAccounting: string;
  includeWeightThreadAccounting: string;
  sloc: string;
}

export const totalKeys: (keyof TpurchaseDetail)[] = [
  'totalGoldWeightThread',
  'q10',
  'measureWeightThread',
  'lossWeightThread',
  'includeWeightThread',
  'totalGoldWeightOfStool',
  'finalPrice',
  'totalWeightThreadAccounting',
  'measureWeightThreadAccounting',
  'lossWeightThreadAccounting',
  'includeWeightThreadAccounting',
  'q10Accounting',
  'totalWeightStoolAccounting',
  'finalPriceAccounting',
];

export const dataIndexes: (keyof TpurchaseDetail)[] = [
  'totalGoldWeightThread',
  'q10',
  'measureWeightThread',
  'lossWeightThread',
  'includeWeightThread',
  'totalGoldWeightOfStool',
  'unitPrice',
  'finalPrice',
  'policyPrice',
  'totalWeightThreadAccounting',
  'measureWeightThreadAccounting',
  'lossWeightThreadAccounting',
  'includeWeightThreadAccounting',
  'q10Accounting',
  'totalWeightStoolAccounting',
  'finalPriceAccounting',
];

export const formatValue = (value: number): number | string => {
  if (value === null || value === undefined) {
    return '-';
  }

  return Number.isInteger(value)
    ? formatNumber(value)
    : formatCurrency({ value, format: 3 });
};

const TablePurchaseInvoiceDetail: React.FC<PurchaseTableProps> = ({
  data,
  editIndex,
  setEditIndex,
  onSave,
  migoList,
  lastMigoStatus,
  lastMigoType,
}: PurchaseTableProps) => {
  // Debug props
  console.log('TablePurchaseInvoiceDetail props:', {
    migoList,
    lastMigoStatus,
    lastMigoType,
    migoListLength: migoList?.length,
    shouldDisable:
      migoList &&
      migoList.length > 0 &&
      lastMigoStatus === true &&
      lastMigoType === '501',
  });
  const [form] = useForm<IPurchaseInvoiceDetailForm>();

  const queryParam = useQueryParam(),
    page = parseInt(queryParam.get('page') + '') || 1,
    pageSize = parseInt(queryParam.get('page_size') + '') || DEFAULT_PAGE_SIZE;

  const handleWeightChange = (
    field: keyof IPurchaseInvoiceDetailForm,
    e: ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = e.target;
    if (/[a-zA-Z\u00C0-\u017F]/.test(value.replace(/,/g, ''))) {
      return;
    }

    const formattedValue = value.replace(/[^0-9,]/g, '');
    const [integerPart, decimalPart] = formattedValue.split(',');

    const formattedIntegerPart = integerPart
      ? parseInt(integerPart, 10).toLocaleString('vi-VN')
      : '';

    const formattedDecimalPart =
      decimalPart !== undefined ? `,${decimalPart.slice(0, 3)}` : '';
    const fieldValue = formattedIntegerPart + formattedDecimalPart;

    form.setFieldValue(field, fieldValue);
  };

  const handleEdit = (index: number, record: TpurchaseDetail) => {
    const {
      includeWeightThreadAccounting,
      lossWeightThreadAccounting,
      measureWeightThreadAccounting,
    } = record;

    form.setFieldsValue({
      includeWeightThreadAccounting: formatCurrency({
        value: includeWeightThreadAccounting,
        format: 3,
      })?.toString(),
      lossWeightThreadAccounting: formatCurrency({
        value: lossWeightThreadAccounting,
        format: 3,
      })?.toString(),
      measureWeightThreadAccounting: formatCurrency({
        value: measureWeightThreadAccounting,
        format: 3,
      })?.toString(),
    });

    setEditIndex(index);
  };

  const columns: ColumnsTypeCustom = useMemo(
    () => [
      {
        title: 'STT',
        render: (_, __, index: number) => index + 1 + pageSize * (page - 1),
      },
      { title: 'Mã NVL', dataIndex: ['tproduct', 'id'] },
      {
        title: 'Tên NVL',
        dataIndex: ['tproduct', 'name'],
      },
      {
        align: 'right',
        title: 'TL(chỉ)',
        dataIndex: 'totalGoldWeightThread',
        // render: (value) => formatCurrency({ value, format: 3 }),
        render: (value) => formatValue(value),
      },
      {
        align: 'right',
        title: 'Vàng Q10 (chỉ)',
        dataIndex: 'q10',
        // render: (value) => formatNumber(value),
        // render: (value) => formatCurrency({ value, format: 3 }),
        render: (value) => formatValue(value),
      },
      {
        align: 'right',
        title: 'TL BAO GỒM HỘT(chỉ)',
        dataIndex: 'measureWeightThread',
        // render: (value) => formatNumber(value),
        // render: (value) => formatCurrency({ value, format: 3 }),
        render: (value) => formatValue(value),
      },
      {
        align: 'right',
        title: 'TL bụi bẩn (chỉ)',
        dataIndex: 'lossWeightThread',
        // render: (value) => formatNumber(value),
        // render: (value) => formatCurrency({ value, format: 3 }),
        render: (value) => formatValue(value),
      },
      {
        align: 'right',
        title: 'TL HỘT(chỉ)',
        dataIndex: 'includeWeightThread',
        // render: (value) => formatNumber(value),
        // render: (value) => formatCurrency({ value, format: 3 }),
        render: (value) => formatValue(value),
      },
      {
        align: 'right',
        title: 'TL (phân)',
        dataIndex: 'totalGoldWeightOfStool',
        // render: (value) => formatNumber(value),
        // render: (value) => formatCurrency({ value, format: 3 }),
        render: (value) => formatValue(value),
      },
      {
        width: 100,
        align: 'right',
        title: 'Đơn giá (phân)',
        dataIndex: 'unitPrice',
        render: (value) =>
          value === null || value === undefined
            ? '-'
            : new Intl.NumberFormat('vi-VN', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(value),
      },
      {
        width: 150,
        align: 'right',
        currency: 'vi-VN',
        title: 'Thành tiền (phân)',
        dataIndex: 'finalPrice',
        render: (value) => formatNumber(value),
      },
      {
        width: 150,
        align: 'right',
        currency: 'vi-VN',
        title: 'Tiền chính sách',
        dataIndex: 'policyPrice',
        render: (value) => formatNumber(value),
        // render: (value) => formatCurrency({ value, format: 3 }),
      },
      {
        align: 'right',
        title: 'TL sau ĐC(chỉ)',
        dataIndex: 'totalWeightThreadAccounting',
        // render: (value) => (value ? formatCurrency({ value, format: 3 }) : '-'),
        render: (value) => formatValue(value),
      },
      {
        align: 'right',
        title: 'TL bao gồm hột ĐC(chỉ)',
        dataIndex: 'measureWeightThreadAccounting',
        render: (value, _, index) => {
          return editIndex === index ? (
            <Form.Item
              className="mb-0"
              name="measureWeightThreadAccounting"
              rules={[
                { required: true, message: 'Vui lòng nhập TL' },
                {
                  pattern: /^[^a-zA-Z\u00C0-\u017F]*$/,
                  message: 'Chỉ được nhập số',
                },
              ]}
            >
              <Input
                onChange={(e) =>
                  handleWeightChange('measureWeightThreadAccounting', e)
                }
              />
            </Form.Item>
          ) : value ? (
            // formatCurrency({ value, format: 3 })
            formatValue(value)
          ) : (
            '-'
          );
        },
      },
      {
        align: 'right',
        title: 'TL bụi bẩn ĐC(chỉ)',
        dataIndex: 'lossWeightThreadAccounting',
        render: (value, _, index) =>
          editIndex === index ? (
            <Form.Item
              className="mb-0"
              name="lossWeightThreadAccounting"
              rules={[
                { required: true, message: 'Vui lòng nhập TL' },
                {
                  pattern: /^[^a-zA-Z\u00C0-\u017F]*$/,
                  message: 'Chỉ được nhập số',
                },
              ]}
            >
              <Input
                onChange={(e) =>
                  handleWeightChange('lossWeightThreadAccounting', e)
                }
              />
            </Form.Item>
          ) : value ? (
            // formatCurrency({ value, format: 3 })
            formatValue(value)
          ) : (
            '-'
          ),
      },
      {
        align: 'right',
        title: 'TL hột ĐC(chỉ)',
        dataIndex: 'includeWeightThreadAccounting',
        render: (value, _, index) =>
          editIndex === index ? (
            <Form.Item
              className="mb-0"
              name="includeWeightThreadAccounting"
              rules={[
                { required: true, message: 'Vui lòng nhập TL' },
                {
                  pattern: /^[^a-zA-Z\u00C0-\u017F]*$/,
                  message: 'Chỉ được nhập số',
                },
              ]}
            >
              <Input
                onChange={(e) =>
                  handleWeightChange('includeWeightThreadAccounting', e)
                }
              />
            </Form.Item>
          ) : value ? (
            // formatCurrency({ value, format: 3 })
            formatValue(value)
          ) : (
            '-'
          ),
      },
      {
        align: 'right',
        title: 'TL Q10 sau ĐC(chỉ)',
        dataIndex: 'q10Accounting',
        // render: (value) => (value ? formatCurrency({ value, format: 3 }) : '-'),
        render: (value) => formatValue(value),
      },
      {
        align: 'right',
        title: 'TL sau ĐC(phân)',
        dataIndex: 'totalWeightStoolAccounting',
        render: (value) => formatValue(value),
        // render: (value) => (value ? formatCurrency({ value, format: 3 }) : '-'),
      },
      {
        align: 'right',
        title: 'Thành tiền sau ĐC',
        dataIndex: 'finalPriceAccounting',
        render: (value) => formatNumber(value),
      },
      {
        title: 'Ngày tạo',
        dataIndex: 'createDate',
        render: (value) => formatDate(value),
      },
      {
        width: 180,
        title: 'Người cập nhật',
        dataIndex: ['tmodifyUser', 'fullName'],
      },
      {
        width: 200,
        title: 'Ngày cập nhật',
        dataIndex: 'modifyDate',
        render: (value: string) =>
          value ? dayjs(value).format('DD/MM/YYYY HH:MM') : '-',
      },
      {
        width: 80,
        fixed: 'right',
        align: 'center',
        render: (_, record, index) => (
          <Space size="middle" className="text-lg">
            {editIndex === index ? (
              <>
                <ButtonAction tooltip="Lưu" onClick={handleSave}>
                  <SaveTwoTone style={{ fontSize: 20 }} />
                </ButtonAction>
                <ButtonAction tooltip="Huỷ" onClick={handleCancel}>
                  <CloseCircleTwoTone style={{ fontSize: 20 }} />
                </ButtonAction>
              </>
            ) : (
              <>
                <ButtonAction
                  tooltip="Chỉnh sửa"
                  title={<EditIcon />}
                  disabled={
                    !!data?.parentId ||
                    data?.statusId === 'OR02_02' ||
                    (migoList &&
                      migoList.length > 0 &&
                      lastMigoStatus === true &&
                      lastMigoType === '501')
                  }
                  onClick={() => handleEdit(index, record)}
                />
              </>
            )}
          </Space>
        ),
      },
    ],
    [data, page, pageSize, editIndex],
  );

  const handleCancel = () => {
    form.resetFields();
    setEditIndex(-1);
  };

  const checkFormValidate = async () => {
    try {
      await form.validateFields();
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleSave = async () => {
    if (!(await checkFormValidate())) return;

    const fieldsValue = form.getFieldsValue();
    const params: IPurchaseInvoiceDetailForm & {
      detailId: string;
    } = { ...fieldsValue, detailId: data?.tpurchaseDetails?.[editIndex]?.id };

    onSave(params);
    handleCancel();
  };

  return (
    <Form form={form}>
      <Table
        columns={columns}
        pagination={false}
        scroll={{ x: 'max-content' }}
        rowKey={(record) => record?.id}
        dataSource={data?.tpurchaseDetails}
        summary={(pageData) => {
          const data = pageData as TPurchaseDetail[];

          if (data.length <= 0) return;
          const total = totalData(data, totalKeys, dataIndexes);

          return (
            <Summary fixed>
              <Summary.Row>
                <Summary.Cell colSpan={3} index={0}>
                  <Text strong>Tổng cộng</Text>
                </Summary.Cell>
                {dataIndexes.map((dataIndex, index) => {
                  const isCellPrice = dataIndex.includes('Price');

                  return (
                    <Summary.Cell
                      key={dataIndex}
                      index={index + 1}
                      className="font-bold text-right"
                    >
                      {/* {formatNumber(total[dataIndex])} */}
                      {/* {formatCurrency({
                        value: total?.[dataIndex],
                        format: !isCellPrice ? 3 : 0,
                      })} */}
                      {formatValue(total?.[dataIndex])}
                    </Summary.Cell>
                  );
                })}
              </Summary.Row>
            </Summary>
          );
        }}
      />
    </Form>
  );
};

export default memo(TablePurchaseInvoiceDetail);
